'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  PasswordCheck,
  PasswordCheckT,
  PasswordInput,
  SubmitButtonWithValidation,
} from '@pioz/mlm-skeleton/form-fields';
import { changePasswordSchema, ChangePasswordT } from '../change-password-schema';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import clsx from 'clsx';
import { CommonLocals } from '@pioz/mlm-common';

export interface CredentialsFormT {
  title: string;
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
  submit: string;
  passwordUpdated: string;
  passwordNotUpdated: string;
}

export function PasswordFormClient({
  locale,
  updateAction,
  className,
  titleClass,
  t,
}: {
  locale: CommonLocals;
  updateAction: (data: FormData) => Promise<void>;
  className?: string;
  titleClass?: string;
  t: { mainT: CredentialsFormT; errorT: ChangePasswordT & PasswordCheckT };
}) {
  const {
    register,
    reset,
    trigger,
    formState: { errors, isValid, isDirty },
    watch,
  } = useForm({
    defaultValues: {
      password_current: '',
      password_new: '',
      password_confirm: '',
    },
    mode: 'all',
    resolver: zodResolver(changePasswordSchema(t.errorT)),
  });

  return (
    <form
      action={async (data) => {
        try {
          await updateAction(data);
          toast.success(t.mainT.passwordUpdated);
          reset();
        } catch (error) {
          toast.error(t.mainT.passwordNotUpdated);
        }
      }}
      className={clsx('flex h-full flex-col', className)}
    >
      <h2 className={titleClass}>{t.mainT.title}</h2>
      <PasswordInput
        required
        {...register('password_current')}
        label={t.mainT.oldPassword}
        error={errors.password_current}
      />
      <PasswordInput
        required
        {...register('password_new')}
        label={t.mainT.newPassword}
        error={errors.password_new}
      />
      <PasswordInput
        required
        {...register('password_confirm')}
        label={t.mainT.confirmPassword}
        error={errors.password_confirm}
      />
      <PasswordCheck
        password={watch('password_new')}
        passwordConfirm={watch('password_confirm')}
        t={t.errorT}
      />
      <div className="mt-auto flex justify-end gap-3">
        <SubmitButtonWithValidation
          locale={locale}
          isValid={isValid}
          isDirty={isDirty}
          trigger={trigger}
          className="btn btn-primary"
        >
          {t.mainT.submit}
        </SubmitButtonWithValidation>
      </div>
    </form>
  );
}
